import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { mrpService, type MrpRun, type MrpDashboardData } from '@/services/mrpService';
import MTSSuggestModal from '@/pages/manufacturing/production-orders/components/MTSSuggestModal';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { toInputDate } from '@/utils/format';
import MrpRunOverlay from './MrpRunOverlay';

const STEP_MS = 650; // how long each calculation step shows while the run is in progress
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const formatDateDisplay = (dateStr?: string | null) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatHorizonDisplay = (run: MrpRun) => {
  if (run.startDate && run.endDate) {
    return `${formatDateDisplay(run.startDate)} — ${formatDateDisplay(run.endDate)}`;
  }
  const start = new Date(run.runDate || run.createdAt || Date.now());
  const end = new Date(start);
  end.setDate(end.getDate() + (run.planningHorizonDays || 30));
  return `${formatDateDisplay(start.toISOString())} — ${formatDateDisplay(end.toISOString())}`;
};

export default function MRPRunPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [runs, setRuns] = useState<MrpRun[]>([]);
  const [dashboard, setDashboard] = useState<MrpDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [showMTSSuggestModal, setShowMTSSuggestModal] = useState(false);
  const [running, setRunning] = useState(false);
  // Run progress overlay: 0 = hidden, n = step n in progress, > steps = finished
  const [simStep, setSimStep] = useState(0);
  const simTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [planningHorizonDays, setPlanningHorizonDays] = useState(30);
  const [fromDate, setFromDate] = useState(() => {
    return toInputDate(new Date());
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return toInputDate(d);
  });
  const [demandOptions, setDemandOptions] = useState({
    openSalesOrders: true,
    productionOrders: true,
    safetyStock: true,
    reorderPoint: true,
  });
  const [runOptions, setRunOptions] = useState({
    autoCreateDraftPO: false,
    autoCreateDraftProdOrder: false,
    considerScheduledReceipts: true,
  });

  const handleQuickDays = (days: number) => {
    setPlanningHorizonDays(days);
    const start = fromDate ? new Date(fromDate) : new Date();
    const end = new Date(start);
    end.setDate(end.getDate() + days);
    setToDate(toInputDate(end));
  };

  // Warehouse State
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const selectedWarehouseName = useWarehouseStore((s) => s.selectedWarehouseName);
  const activeWarehouseId = selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined;

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [runsData, dashboardData] = await Promise.all([
        mrpService.getRuns(activeWarehouseId ? { warehouseId: activeWarehouseId } : undefined),
        mrpService.getDashboard(activeWarehouseId ? { warehouseId: activeWarehouseId } : undefined),
      ]);

      let formattedRuns: MrpRun[] = [];

      if (runsData && runsData.length > 0) {
        formattedRuns = runsData.map((r, idx) => {
          const runDateObj = new Date(r.runDate || r.createdAt || Date.now());
          const year = isNaN(runDateObj.getTime()) ? new Date().getFullYear() : runDateObj.getFullYear();
          const seq = String(runsData.length - idx).padStart(3, '0');
          const runNumber = r.runNumber || `MRP-${year}-${seq}`;

          const itemsCount = Number(r.itemsCount) || 0;
          const shortagesCount = Number(r.shortagesCount) || 0;
          const suggestionsCount = Number(r.suggestionsCount) || 0;

          return {
            ...r,
            runNumber,
            note: r.note,
            status: r.status === 'COMPLETED' || r.status === 'APPROVED' ? 'COMPLETED' : r.status,
            itemsCount,
            shortagesCount,
            suggestionsCount,
            shortageDelta: r.shortageDelta ?? null,
          };
        });
      } else {
        formattedRuns = [];
      }

      setRuns(formattedRuns);
      setDashboard(dashboardData);
    } catch (err) {
      console.error('Failed to load MRP data:', err);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [activeWarehouseId]);

  useEffect(() => {
    load();
  }, [load]);

  // "Creating draft orders" only when the run will actually create them
  const runSteps = useMemo(() => [
    'Analyzing demand...',
    'Checking current stock...',
    'Calculating net requirements...',
    'Generating suggestions...',
    ...(runOptions.autoCreateDraftPO || runOptions.autoCreateDraftProdOrder ? ['Creating draft orders...'] : []),
  ], [runOptions.autoCreateDraftPO, runOptions.autoCreateDraftProdOrder]);

  const stopSimTimer = () => {
    if (simTimer.current) clearInterval(simTimer.current);
    simTimer.current = null;
  };
  useEffect(() => stopSimTimer, []);

  const handleRunMRP = useCallback(async () => {
    setRunning(true);
    // Steps advance while the server calculates, but the last one waits for the real result
    const startedAt = Date.now();
    setSimStep(1);
    stopSimTimer();
    simTimer.current = setInterval(() => {
      setSimStep((s) => Math.min(s + 1, runSteps.length));
    }, STEP_MS);
    try {
      const result = await mrpService.run({
        planningHorizonDays,
        fromDate,
        toDate,
        warehouseId: activeWarehouseId,
        demandOptions,
        runOptions,
      });
      // A fast run still walks through every step before finishing, then holds the completed state briefly
      const minMs = runSteps.length * STEP_MS;
      const elapsed = Date.now() - startedAt;
      if (elapsed < minMs) await sleep(minMs - elapsed);
      stopSimTimer();
      setSimStep(runSteps.length + 1);
      await sleep(900);
      setSimStep(0);

      const auto = result.autoCreated;
      const autoNote = [
        auto?.purchaseOrders?.length ? `${auto.purchaseOrders.length} draft purchase order(s)` : '',
        auto?.productionOrders?.length ? `${auto.productionOrders.length} draft production order(s)` : '',
      ].filter(Boolean).join(' and ');
      const productionCount = result.productionSuggestions?.length ?? 0;
      const runs = result.runs || [];
      // All-warehouse runs plan each warehouse separately — list what each one needs
      const perWarehouse = runs.length > 1
        ? ' ' + runs
            .filter((r) => r.purchaseSuggestionCount + r.productionSuggestionCount > 0)
            .map((r) => `${r.warehouseName}: ${r.purchaseSuggestionCount} purchase / ${r.productionSuggestionCount} production`)
            .join(' · ')
        : '';
      toast.success(
        `MRP run complete${runs.length > 1 ? ` for ${runs.length} warehouses` : ''} — ${result.plannedOrders.length} purchase suggestion(s), ${productionCount} production suggestion(s).${perWarehouse}${autoNote ? ` Created ${autoNote}.` : ''}`
      );
      if (auto?.skipped?.length) {
        toast.warning(`Not auto-created: ${auto.skipped.join('; ')}`);
      }
      setModalOpen(false);
      await load();
      if (result.mrpRunId) navigate(`/manufacturing/mrp/shortage?runId=${result.mrpRunId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to run MRP';
      toast.error(message);
    } finally {
      stopSimTimer();
      setSimStep(0);
      setRunning(false);
    }
  }, [planningHorizonDays, fromDate, toDate, activeWarehouseId, demandOptions, runOptions, runSteps, load, navigate, toast]);

  // The very top/latest run
  const lastRun = useMemo(() => {
    return runs.length > 0 ? runs[0] : null;
  }, [runs]);

  // Filtered runs based on search & status
  const filteredRuns = useMemo(() => {
    return runs.filter((r) => {
      const matchesSearch =
        !searchTerm.trim() ||
        (r.runNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
        (r.note?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
        (r.runByName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

      const matchesStatus =
        statusFilter === 'ALL' ||
        r.status?.toUpperCase() === statusFilter.toUpperCase();

      return matchesSearch && matchesStatus;
    });
  }, [runs, searchTerm, statusFilter]);

  // Shortage Trend (Last up to 3 Runs)
  const trendRuns = useMemo(() => {
    if (!runs || runs.length === 0) return [];
    const recent = runs.slice(0, 3).reverse();
    return recent.map((r, idx, arr) => {
      const prev = idx > 0 ? arr[idx - 1] : null;
      const count = Number(r.shortagesCount) || 0;
      const prevCount = prev ? Number(prev.shortagesCount) || 0 : count;
      return {
        suffix: r.runNumber?.split('-').pop() || String(idx + 1).padStart(3, '0'),
        count,
        isIncrease: count > prevCount,
        color: count > 5 ? 'coral' : 'amber',
      };
    });
  }, [runs]);

  const maxTrendCount = useMemo(() => {
    if (trendRuns.length === 0) return 10;
    return Math.max(...trendRuns.map((t) => t.count), 1);
  }, [trendRuns]);

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#0f172a] tracking-tight">
                Material Requirements Planning
              </h1>
              {selectedWarehouseName && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/80 shadow-xs">
                  <i className="ri-store-2-line text-sm text-indigo-600" />
                  <span>{selectedWarehouseName}</span>
                </span>
              )}
            </div>
            <p className="text-sm text-[#64748b] mt-1">
              Plan production and procurement based on demand
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4f46e5] hover:bg-[#4338ca] text-white text-sm font-semibold rounded-lg shadow-sm transition-colors cursor-pointer whitespace-nowrap self-start sm:self-auto"
          >
            <i className="ri-play-fill text-sm" />
            <span>Run MRP Now</span>
          </button>
        </div>

        {/* Last Run Card */}
        {lastRun ? (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5 mb-5 shadow-xs">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <span className="text-xs text-[#94a3b8] font-medium block mb-1">
                  Last Run
                </span>
                <h2 className="text-lg font-bold text-[#0f172a] mb-2.5">
                  {lastRun.runNumber} on {formatDateDisplay(lastRun.runDate)}
                </h2>
                <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-[#475569]">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-stack-line text-sm text-[#94a3b8]" />
                    <span>{lastRun.itemsCount ?? 0} items</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-sm text-[#f59e0b]" />
                    <span>{lastRun.shortagesCount ?? 0} shortages</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-lightbulb-line text-sm text-[#6366f1]" />
                    <span>{lastRun.suggestionsCount ?? 0} suggestions</span>
                  </div>
                  {lastRun.warehouseName ? (
                    <div className="flex items-center gap-1 text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-2.5 py-0.5 rounded-full font-semibold">
                      <i className="ri-store-2-line text-xs" />
                      <span>{lastRun.warehouseName}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-full font-medium">
                      <span>All Warehouses</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                  {lastRun.status || 'COMPLETED'}
                </span>
                <button
                  onClick={() => navigate(`/manufacturing/mrp/shortage?runId=${lastRun.id}`)}
                  className="px-4 py-2 text-xs font-semibold text-[#4f46e5] bg-[#eef2ff] hover:bg-[#e0e7ff] rounded-lg transition-colors cursor-pointer"
                >
                  View Results
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Data changed since the last run — its suggestions are out of date */}
        {lastRun && (dashboard?.changesSinceRun?.total || 0) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2.5 text-xs text-amber-900">
              <i className="ri-refresh-alert-line text-base text-amber-600 shrink-0" />
              <div>
                <p className="font-bold">Data has changed since the last MRP run</p>
                <p className="mt-0.5 text-amber-800">
                  {[
                    [dashboard!.changesSinceRun!.salesOrders, 'sales order'],
                    [dashboard!.changesSinceRun!.productionOrders, 'production order'],
                    [dashboard!.changesSinceRun!.purchaseOrders, 'purchase order'],
                    [dashboard!.changesSinceRun!.goodsReceipts, 'goods receipt'],
                  ]
                    .filter(([n]) => Number(n) > 0)
                    .map(([n, label]) => `${n} ${label}${Number(n) === 1 ? '' : 's'}`)
                    .join(' · ')}{' '}
                  created or updated. Run MRP again to refresh shortages and suggestions.
                </p>
              </div>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors shrink-0 cursor-pointer"
            >
              <i className="ri-play-fill text-xs" /> Run MRP Again
            </button>
          </div>
        )}

        {lastRun ? null : !loading ? (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5 mb-5 shadow-xs flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl shrink-0">
                <i className="ri-store-2-line" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">
                  No MRP Runs Recorded for {selectedWarehouseName || 'This Warehouse'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Click &ldquo;Run MRP Now&rdquo; to analyze demand and generate procurement suggestions for {selectedWarehouseName || 'the warehouse'}.
                </p>
              </div>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors shrink-0"
            >
              <i className="ri-play-fill text-xs" /> Run MRP Now
            </button>
          </div>
        ) : null}

        {/* Filter Bar */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-3.5 mb-5 shadow-xs flex flex-wrap items-center gap-3">
          <div className="relative flex items-center w-full max-w-sm border border-[#e2e8f0] rounded-lg px-3 py-2 bg-white">
            <i className="ri-search-line text-[#94a3b8] text-sm mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Search by run number or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs sm:text-sm text-[#1e293b] placeholder:text-[#94a3b8] bg-transparent outline-none"
            />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none border border-[#e2e8f0] rounded-lg pl-3 pr-8 py-2 text-xs sm:text-sm text-[#334155] bg-white outline-none cursor-pointer"
            >
              <option value="ALL">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="DRAFT">Draft</option>
              <option value="APPROVED">Approved</option>
              <option value="CLOSED">Closed</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8] pointer-events-none" />
          </div>
        </div>

        {/* Shortage Trend (Last 3 Runs) Card */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 mb-5 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0f172a]">
              Shortage Trend (Last 3 Runs)
            </h3>
            <span className="text-xs text-[#94a3b8]">
              Bar height = shortage count
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end pt-2 pb-1">
            {trendRuns.map((t, idx) => {
              const barHeight = Math.max(24, Math.round((t.count / maxTrendCount) * 54));
              return (
                <div key={idx} className="flex flex-col items-center justify-end">
                  <div className="mb-1.5 flex items-center justify-center gap-1">
                    <span
                      className={`text-xs font-bold ${t.isIncrease ? 'text-[#ef4444]' : 'text-[#1e293b]'
                        }`}
                    >
                      {t.count}
                    </span>
                    {t.isIncrease && (
                      <span className="text-[11px] font-bold text-[#ef4444]">↑</span>
                    )}
                  </div>
                  <div
                    style={{ height: `${barHeight}px` }}
                    className={`w-full rounded-md transition-all duration-300 ${t.color === 'amber' ? 'bg-[#f59e0b]' : 'bg-[#f87171]'
                      }`}
                  />
                  <span className="text-xs text-[#94a3b8] mt-2 font-mono">
                    {t.suffix}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Runs Table */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-xs font-semibold text-[#475569]">
                  <th className="text-left px-5 py-3.5">Run #</th>
                  <th className="text-left px-4 py-3.5">Warehouse</th>
                  <th className="text-left px-4 py-3.5">Date</th>
                  <th className="text-left px-4 py-3.5">Horizon</th>
                  <th className="text-center px-4 py-3.5">Items</th>
                  <th className="text-center px-4 py-3.5">Shortages</th>
                  <th className="text-center px-4 py-3.5">Suggestions</th>
                  <th className="text-center px-4 py-3.5">Status</th>
                  <th className="text-left px-5 py-3.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRuns.map((run) => {
                  const isCompleted =
                    run.status?.toUpperCase() === 'COMPLETED' ||
                    run.status?.toUpperCase() === 'APPROVED';

                  const shortages = Number(run.shortagesCount) || 0;

                  return (
                    <tr
                      key={run.id}
                      className="hover:bg-slate-50/70 transition-colors"
                    >
                      {/* Run # */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/manufacturing/mrp/shortage?runId=${run.id}`)
                          }
                          className="font-semibold text-[#4f46e5] hover:underline cursor-pointer text-left block text-sm whitespace-nowrap"
                        >
                          {run.runNumber}
                        </button>
                        {run.note && (
                          <span className="text-xs text-[#94a3b8] block mt-0.5 whitespace-nowrap">
                            {run.note}
                          </span>
                        )}
                      </td>

                      {/* Warehouse */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {run.warehouseName ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200/80">
                            <i className="ri-store-2-line text-xs text-indigo-500" />
                            <span>{run.warehouseName}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            All Warehouses
                          </span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-4 py-4 text-[#334155] whitespace-nowrap text-sm">
                        {formatDateDisplay(run.runDate || run.createdAt)}
                      </td>

                      {/* Horizon */}
                      <td className="px-4 py-4 text-[#475569] whitespace-nowrap text-sm">
                        {formatHorizonDisplay(run)}
                      </td>

                      {/* Items */}
                      <td className="px-4 py-4 text-center text-[#334155] font-semibold text-sm">
                        {run.itemsCount ?? 0}
                      </td>

                      {/* Shortages */}
                      <td className="px-4 py-4 text-center whitespace-nowrap">
                        {shortages > 0 ? (
                          <span className="inline-flex items-center justify-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-semibold bg-[#fef2f2] text-[#ef4444] border border-[#fecaca] mx-auto">
                            <span>{shortages}</span>
                            {run.shortageDelta && run.shortageDelta > 0 ? (
                              <span className="text-[11px] font-bold">
                                ↑ +{run.shortageDelta}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 mx-auto">
                            0
                          </span>
                        )}
                      </td>

                      {/* Suggestions */}
                      <td className="px-4 py-4 text-center text-[#334155] font-semibold text-sm">
                        {run.suggestionsCount ?? 0}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 text-center">
                        {isCompleted ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#fffbeb] text-[#d97706] border border-[#fde68a]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]" />
                            {run.status || 'Draft'}
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/manufacturing/mrp/shortage?runId=${run.id}`)
                            }
                            className="px-3 py-1.5 text-xs font-medium text-[#334155] bg-[#f1f5f9] hover:bg-[#e2e8f0] rounded-md transition-colors cursor-pointer"
                          >
                            View Results
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/manufacturing/mrp/purchase?runId=${run.id}`)
                            }
                            className="px-3 py-1.5 text-xs font-medium text-[#334155] bg-[#f1f5f9] hover:bg-[#e2e8f0] rounded-md transition-colors cursor-pointer"
                          >
                            View Suggestions
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && filteredRuns.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-5 py-12 text-center text-slate-400 text-sm"
                    >
                      No MRP runs found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Configure MRP Run Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base sm:text-lg font-bold text-[#0f172a]">
                Configure MRP Run
              </h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md transition-colors cursor-pointer text-lg leading-none"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              {/* TARGET WAREHOUSE CARD */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                    <i className="ri-store-2-line text-lg" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Target Warehouse</span>
                    <span className="text-sm font-bold text-slate-800">{activeWarehouseId ? selectedWarehouseName : 'All Warehouses'}</span>
                    <span className="text-[11px] text-slate-500 block mt-0.5">
                      {activeWarehouseId
                        ? 'Plans only this warehouse’s stock, open orders and demand'
                        : 'Runs a separate MRP for each warehouse — stock is never pooled across warehouses'}
                    </span>
                  </div>
                </div>
                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 rounded-full">
                  Active
                </span>
              </div>
              {/* PLANNING HORIZON */}
              <div>
                <label className="text-xs font-bold text-[#334155] tracking-wider uppercase block mb-2.5">
                  PLANNING HORIZON
                </label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <span className="text-xs text-[#64748b] font-medium mb-1 block">
                      From Date
                    </span>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        if (e.target.value && toDate) {
                          const diff = Math.round(
                            (new Date(toDate).getTime() - new Date(e.target.value).getTime()) /
                            (1000 * 60 * 60 * 24)
                          );
                          if (diff > 0) setPlanningHorizonDays(diff);
                        }
                      }}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#1e293b] bg-white outline-none focus:border-indigo-500 cursor-pointer"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-[#64748b] font-medium mb-1 block">
                      To Date
                    </span>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        if (fromDate && e.target.value) {
                          const diff = Math.round(
                            (new Date(e.target.value).getTime() - new Date(fromDate).getTime()) /
                            (1000 * 60 * 60 * 24)
                          );
                          if (diff > 0) setPlanningHorizonDays(diff);
                        }
                      }}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#1e293b] bg-white outline-none focus:border-indigo-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Quick days pills */}
                <div className="flex items-center gap-2">
                  {[30, 60, 90].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => handleQuickDays(days)}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-[#eef2ff] hover:bg-[#e0e7ff] text-[#4f46e5] transition-colors cursor-pointer"
                    >
                      {days} Days
                    </button>
                  ))}
                </div>
              </div>

              {/* INCLUDE IN DEMAND */}
              <div>
                <label className="text-xs font-bold text-[#334155] tracking-wider uppercase block mb-2.5">
                  INCLUDE IN DEMAND
                </label>
                <div className="space-y-2.5">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={demandOptions.openSalesOrders}
                      onChange={(e) =>
                        setDemandOptions((prev) => ({
                          ...prev,
                          openSalesOrders: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Open Sales Orders (pending invoices)
                    </span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={demandOptions.productionOrders}
                      onChange={(e) =>
                        setDemandOptions((prev) => ({
                          ...prev,
                          productionOrders: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Production Orders (material needs)
                    </span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={demandOptions.safetyStock}
                      onChange={(e) =>
                        setDemandOptions((prev) => ({
                          ...prev,
                          safetyStock: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Safety Stock Requirements
                    </span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={demandOptions.reorderPoint}
                      onChange={(e) =>
                        setDemandOptions((prev) => ({
                          ...prev,
                          reorderPoint: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Reorder Point Triggers
                    </span>
                  </label>
                </div>
              </div>

              {/* OPTIONS */}
              <div>
                <label className="text-xs font-bold text-[#334155] tracking-wider uppercase block mb-2.5">
                  OPTIONS
                </label>
                <div className="space-y-2.5">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={runOptions.autoCreateDraftPO}
                      onChange={(e) =>
                        setRunOptions((prev) => ({
                          ...prev,
                          autoCreateDraftPO: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Auto-create Draft Purchase Orders for shortages
                    </span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={runOptions.autoCreateDraftProdOrder}
                      onChange={(e) =>
                        setRunOptions((prev) => ({
                          ...prev,
                          autoCreateDraftProdOrder: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Auto-create Draft Production Orders for shortages
                    </span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={runOptions.considerScheduledReceipts}
                      onChange={(e) =>
                        setRunOptions((prev) => ({
                          ...prev,
                          considerScheduledReceipts: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded text-[#4f46e5] accent-[#4f46e5] border-slate-300 cursor-pointer"
                    />
                    <span className="text-sm text-[#1e293b]">
                      Consider scheduled receipts (open POs)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-5 py-2 text-sm font-semibold text-[#334155] bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRunMRP}
                disabled={running}
                className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-[#4f46e5] hover:bg-[#4338ca] rounded-lg transition-colors disabled:opacity-50 cursor-pointer shadow-xs"
              >
                {running ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Running...
                  </>
                ) : (
                  <>
                    <i className="ri-play-fill text-xs" /> Run MRP
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MRP run progress (above the configure modal) */}
      <MrpRunOverlay open={simStep > 0} steps={runSteps} step={simStep} />

      {/* MTS Suggestions Modal */}
      {showMTSSuggestModal && (
        <MTSSuggestModal
          onClose={() => setShowMTSSuggestModal(false)}
          onCreated={() => {
            load();
            toast.success('MTS Production Orders created successfully');
          }}
        />
      )}
    </AppLayout>
  );
}
